import React, { useState, useRef, useEffect } from "react";
import {
  CheckCircleOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UpOutlined,
  DownOutlined,
  DatabaseOutlined,
  RightOutlined,
} from "@ant-design/icons";
import DashboardBlack from '../../../assets/DashboardImages/dashblack.png';
import Dashboard from '../../../assets/DashboardImages/dashboard.png';
import { Link } from "react-router-dom";

function useDarkMode() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  return isDark;
}

function SolutionSection() {
  const isDarkMode = useDarkMode();
  const features = [
    {
      title: "Performance Monitoring",
      description:
        "Continuous Monitoring and Immediate Data Analysis for Real-time Insights.",
      icon: <CheckCircleOutlined />,
      viewMoreData: "/performance-monitoring",
    },
    {
      title: "Predictive Analytics",
      description:
        "Unlock the Future with Predictive Analytics: Where Data Meets Destiny.",
      icon: <ThunderboltOutlined />,
      viewMoreData: "/predictive-analytics",
    },
    {
      title: "Customised Solutions",
      description: "Your Vision, Our Precision: Custom Solutions Redefined!",
      icon: <DatabaseOutlined />,
      viewMoreData: "/customised-solutions",
    },
    {
      title: "Incident Assistant",
      description:
        "Empower your teams to efficiently resolve incidents by providing insights and solutions based on system data.",
      icon: <ToolOutlined />,
      viewMoreData: "/incident-assistant",
    },
  ];
  
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const sectionRef = useRef(null);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  const handleViewMore = (event, viewMoreData) => {
    event.stopPropagation();
    console.log("Navigating to:", viewMoreData);
  };

  useEffect(() => {
    const observerOptions = {
      threshold: 0.3,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      });
    }, observerOptions);

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className={`w-full px-4 xs:px-4 sm:px-6 md:px-8 lg:px-12 py-10 xs:py-10 sm:py-12 md:py-16 transition-opacity duration-1000 transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 xs:mb-10 sm:mb-12">
          <h2 className="text-3xl xs:text-4xl sm:text-4xl md:text-5xl lg:text-6xl text-gray-900 dark:text-white mb-4 text-center md:text-center sm:text-start xs:text-start">
            Our <span className="relative inline-block">Solutions</span>
          </h2>
          <p className="text-lg xs:text-xl sm:text-2xl md:text-3xl text-gray-600 dark:text-gray-400 text-center md:text-center sm:text-start xs:text-start">
            Coupled with our machine monitoring technology, our solutions pave
            the way for data-driven decision-making, predictive maintenance, and
            enhanced operational excellence.
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 xs:gap-8 sm:gap-10 md:gap-12">
          
          {/* Image */}
          <div className="w-full md:w-1/2">
            <img
              src={isDarkMode ? DashboardBlack : Dashboard}
              alt="Dashboard"
              className="w-full h-auto rounded-2xl xs:rounded-2xl sm:rounded-3xl shadow-xl"
            />
          </div>

          {/* Features List */}
          <div className="w-full md:w-1/2 space-y-2 xs:space-y-2 sm:space-y-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-3 xs:p-4 sm:p-5 bg-white dark:bg-transparent rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => toggleExpand(index)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 xs:gap-3">
                    <div className="text-xl xs:text-2xl sm:text-2xl md:text-3xl text-blue-400">
                      {feature.icon}
                    </div>
                    <h3 className="text-base xs:text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-black dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <div>
                    {expandedIndex === index ? (
                      <UpOutlined className="text-black dark:text-white text-sm xs:text-base" />
                    ) : (
                      <DownOutlined className="text-gray-400 text-sm xs:text-base" />
                    )}
                  </div>
                </div>
                {expandedIndex === index && (
                  <div className="mt-3 xs:mt-4 text-gray-600 dark:text-gray-400">
                    <p className="text-sm xs:text-base sm:text-base md:text-lg">
                      {feature.description}
                    </p>
                    <div className="mt-3 xs:mt-4">
                      <Link
                        to={feature.viewMoreData}
                        onClick={(event) =>
                          handleViewMore(event, feature.viewMoreData)
                        }
                        className="inline-flex items-center text-sm xs:text-base text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-500"
                      >
                        View More <RightOutlined className="ml-2" />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SolutionSection;
